import React, { useState, useEffect, useContext, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { DropDown, DropDownOptionsProps } from "../../ui/DropDown";
import { GetHook } from "../../api/api";
import { COMPANY_URL } from "../../api/urls";
import { parseDropdown } from "../../utils/parseDropdown";
import { AuthContext } from "../../context/AuthContext";
import { TitleText } from "../../ui/Text";
import { get } from "../../utils/get";
import { useLocation, useRouteMatch, Route, Link } from "react-router-dom";
import SearchInput from "../../ui/SearchInput";
import useAuth from "../../hooks/useAuth";

export const Header = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const { data, error, pending, reload } = GetHook(COMPANY_URL);
  const auth = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchIconVisible, setSearchIconVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleSearch = () => {
    // Implement search functionality
  };

  const handleLogout = () => {
    auth.logout();
  };

  useEffect(() => {
    setCompanies(data);
  }, [data]);

  useEffect(() => {
    const isRouteMatch = location.pathname === `${url}/settings/bscSettings/scorecardConfig` ||
     location.pathname === `${url}/settings/bscSettings/scorecardConfig/perspective/:id` ||
     location.pathname === `${url}/settings/bscSettings/scorecardConfig/perspective/:id/objective/:id`;

    setSearchIconVisible(isRouteMatch);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const options: DropDownOptionsProps = parseDropdown(companies);

  return (
    <HeaderContainer>
      <SearchSection>
        {isSearchIconVisible && (
          <SearchInputContainer>
            <SearchInput handleChange={handleSearch} />
          </SearchInputContainer>
        )}
      </SearchSection>
      <UserSection>
        <UserName>{get(auth, "authState.user.names")}</UserName>
        <UserImageWrapper ref={menuRef}>
          <UserImage
            src={
              get(auth, "authState.user.avatar_url") ||
              `https://ui-avatars.com/api/?name=${get(auth, "authState.user.names")}`
            }
            alt="User Avatar"
            onClick={() => setMenuOpen(!menuOpen)}
          />
          {menuOpen && (
            <Menu>
              <Route>
                {(props) => {
                  const matches = props.location.pathname === `${url}/profile`;
                  return (
                    <MenuLink to={`${url}/settings/profile`} onClick={() => setMenuOpen(false)}>
                      <MenuItem isActive={matches}>
                        <IconSpan>👤</IconSpan> Profile
                      </MenuItem>
                    </MenuLink>
                  );
                }}
              </Route>
              <Route>
                {(props) => {
                  const matches = props.location.pathname === `${url}/settings`;
                  return (
                    <MenuLink to={`${url}/settings`} onClick={() => setMenuOpen(false)}>
                      <MenuItem isActive={matches}>
                        <IconSpan>⚙️</IconSpan> Settings
                      </MenuItem>
                    </MenuLink>
                  );
                }}
              </Route>
              <Route>
                {(props) => {
                    const matches = props.location.pathname === `${url}/help`;
                    return (
                      <MenuLink to={`${url}/help`} onClick={() => setMenuOpen(false)}>
                        <MenuItem isActive={matches}>
                        <IconSpan>❓</IconSpan> Help & Support
                        </MenuItem>
                      </MenuLink>
                    );
                  }}
              </Route>
              <MenuDivider />
              <MenuItem onClick={handleLogout}>
                <IconSpan>🚪</IconSpan> Logout
              </MenuItem>
            </Menu>
          )}
        </UserImageWrapper>
      </UserSection>
    </HeaderContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Menu = styled.div`
  position: absolute;
  top: 50px;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  padding: 8px 0;
  animation: ${fadeIn} 0.2s ease-out;
`;

const MenuItem = styled.div<{ isActive?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  color: ${props => props.isActive ? '#007bff' : '#333'};
  background-color: ${props => props.isActive ? '#f0f0f0' : 'transparent'};
  display: flex;
  align-items: center;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: #f0f0f0;
    color: #007bff;
  }
`;


const IconSpan = styled.span`
  margin-right: 10px;
  font-size: 16px;
`;

const MenuDivider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 8px 0;
`;

const UserImageWrapper = styled.div`
  position: relative;
  cursor: pointer;
`;

const UserImage = styled.img`
  border: 2px solid #ffffff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1em 2em;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background-color: #fff;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  position: relative; /* Needed for positioning the menu */
`;

const UserName = styled(TitleText)`
  font-weight: 500;
  color: #333;
  margin-right: 1em;
`;

const SearchSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const MenuLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;